import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { IColumnPreferences } from './columnPreferences.types';
import {
  buildDefaultPreferences,
  filterColumnsBySearch,
  getShowAllState,
  mergeWithLockedColumns,
  reconcilePreferences,
  reorder
} from './columnOrder.utils';
import {
  loadColumnPreferences,
  saveColumnPreferences,
  clearColumnPreferences
} from './columnPreferences.storage';
import {
  useGetColumnPreferences,
  useSaveColumnPreferences
} from '../../core/modules/book/hooks/useBrendaColumnPreferences';

interface IUseColumnPreferencesArgs {
  tableId: string;
  allColumns: string[];
  lockedColumns: string[];
  isOpen: boolean;
  roleDefaultPreferences?: IColumnPreferences | null;
}

export const useColumnPreferences = ({
  tableId,
  allColumns,
  lockedColumns,
  isOpen,
  roleDefaultPreferences = null
}: IUseColumnPreferencesArgs) => {
  // When a role-based default is provided, it becomes the modal's baseline (and
  // the "Reset to default" target); otherwise every column is shown by default.
  const defaultPreferences = useMemo(
    () => roleDefaultPreferences ?? buildDefaultPreferences(allColumns, lockedColumns),
    [roleDefaultPreferences, allColumns, lockedColumns]
  );

  const [order, setOrder] = useState<string[]>(defaultPreferences.order);
  const [hidden, setHidden] = useState<string[]>(defaultPreferences.hidden);
  const [search, setSearch] = useState<string>('');
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const queryClient = useQueryClient();
  const saveMutation = useSaveColumnPreferences();
  const { data: savedPrefs } = useGetColumnPreferences(tableId, { isOpen });

  // Re-hydrate from storage every time the modal opens, so in-progress
  // edits are discarded on Cancel and the modal always opens fresh.
  useEffect(() => {
    if (!isOpen) {
      return; }

    // Reconcile the saved preferences against the current schema: drop columns
    // that no longer exist and append columns added since the last save. Shared
    // with the table render path (Settings) so both stay consistent. When the
    // user has no saved preferences, fall back to the role-based default so the
    // modal opens reflecting exactly what the table currently shows.
    const saved = savedPrefs || loadColumnPreferences(tableId) || defaultPreferences;
    const { order: reconciledOrder, hidden: reconciledHidden } = reconcilePreferences(
      allColumns,
      lockedColumns,
      saved
    );

    setOrder(reconciledOrder);
    setHidden(reconciledHidden);
    setSearch('');
    setDragIndex(null);
  }, [isOpen, tableId, allColumns, lockedColumns, savedPrefs, defaultPreferences]);

  const filteredOrder = useMemo(() => filterColumnsBySearch(order, search), [order, search]);

  const showAllState = useMemo(() => getShowAllState(order, hidden), [order, hidden]);

  const toggleColumn = (columnName: string) => {
    setHidden(current =>
      current.includes(columnName)
        ? current.filter(col => col !== columnName)
        : [...current, columnName]
    );
  };

  const toggleShowAll = () => {
    setHidden(showAllState === 'all' ? [...order] : []);
  };

  const handleDragStart = (index: number) => setDragIndex(index);

  const handleDragEnter = (targetIndex: number) => {
    if (dragIndex === null || dragIndex === targetIndex) {
      return; }

    // Dragging happens against the (possibly search-filtered) visible list,
    // so map those indices back to real positions in the full order before
    // reordering the underlying state.
    const draggedColumn = filteredOrder[dragIndex];
    const targetColumn = filteredOrder[targetIndex];

    setOrder(current =>
      reorder(current, current.indexOf(draggedColumn), current.indexOf(targetColumn))
    );
    setDragIndex(targetIndex);
  };

  const handleDragEnd = () => setDragIndex(null);

  const resetToDefault = () => {
    setOrder(defaultPreferences.order);
    setHidden(defaultPreferences.hidden);
    setSearch('');
  };

  const commit = async () => {
    const preferences: IColumnPreferences = {
      order,
      hidden
    };

    // Persist to the backend first. Only mirror to localStorage once the save
    // succeeds, so a failed save can't leave stale preferences behind that get
    // re-applied on the next reload (the UI reports the failure to the user).
    await saveMutation.mutateAsync({
      table_id: tableId,
      order,
      hidden
    });

    // Local mirror acts as an offline/refresh fallback for the backend value.
    saveColumnPreferences(tableId, preferences);

    // We already know the value we just saved, so write it straight into the
    // query cache instead of invalidating and awaiting a refetch. Awaiting a
    // refetch here left a ~1s window in which the modal stayed open, the table
    // still showed the old layout, and the Save spinner had already stopped
    // (it only tracks the POST) - making the button look clickable again.
    // setQueryData updates every subscriber synchronously and consistently.
    queryClient.setQueryData<IColumnPreferences>(
      ['brenda-column-preferences', tableId],
      preferences
    );

    return {
      hidden,
      fullOrder: mergeWithLockedColumns(
        allColumns,
        lockedColumns,
        order
      )
    };
  };

  const resetSavedPreferences = () => {
    clearColumnPreferences(tableId);
    queryClient.setQueryData(['brenda-column-preferences', tableId], null);
  };

  return {
    search,
    setSearch,
    filteredOrder,
    hidden,
    showAllState,
    dragIndex,
    toggleColumn,
    toggleShowAll,
    handleDragStart,
    handleDragEnter,
    handleDragEnd,
    resetToDefault,
    resetSavedPreferences,
    commit,
    isSaving: saveMutation.isLoading
  };
};